import React, {
    useEffect, useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    cn
} from 'shared/lib/utils';
import {
    X, Maximize2, Minimize2
} from 'lucide-react';
import AssistantTestPanel from './AssistantTestPanel';
import OpcodeTestPanel from './OpcodeTestPanel';

/**
 * TestDock — persistent test surface for assistants and opcodes.
 * Per A-0012.02 SPEC §2.7 + UI-MAPPING.md §3.
 *
 * Two modes (segmented control), both form-driven (no chat):
 *   - Assistant test: schema-driven typed_input form (context, propensity,
 *     intent, entity) → A2A SendMessage. (AssistantTestPanel.jsx)
 *   - OpCode test: schema-driven inputs form → live NDJSON stream via
 *     useOpcodeStream. (OpcodeTestPanel.jsx)
 *
 * Maximize: when `maximized` is true the dock takes the full workspace
 * canvas (parent must hide the rest). Both panels switch from a single
 * column (form on top, output below) to a 2-pane layout (form left,
 * output right) — much more breathing room.
 *
 * No new endpoints (UF-2). Mode is preserved across open/close; the
 * active panel is mounted/unmounted on tab switch so background WS
 * connections don't accumulate.
 */
export default function TestDock({
    assistant,
    expandedOpcode,
    opcodeMeta,
    defaultTestMode = 'assistant',
    onClose,
    maximized = false,
    onMaximizeToggle,
    isDirty = false,
    onSaveFirst,
    refreshSignal = 0
}) {
    const opcodeTabAvailable = !!expandedOpcode || (assistant?.opcodes?.length || 0) > 0;
    const [mode, setMode] = useState(() => {
        if (expandedOpcode) return 'opcode';
        if (defaultTestMode === 'opcode' && opcodeTabAvailable) return 'opcode';
        return 'assistant';
    });

    // Auto-switch to OpCode mode when editing an inline opcode.
    useEffect(() => {
        if (expandedOpcode) setMode('opcode');
    }, [expandedOpcode]);

    // When navigating to the OpCodes section, prefer OpCode test (once per visit).
    useEffect(() => {
        if (defaultTestMode === 'opcode' && opcodeTabAvailable) setMode('opcode');
    }, [defaultTestMode]); // eslint-disable-line react-hooks/exhaustive-deps -- only on section change

    return (
        <aside className={cn(
            'flex h-full flex-col border-l bg-card',
            maximized ? 'w-full' : 'w-full md:w-[440px]'
        )}>
            {/* Header */}
            <div className="flex flex-shrink-0 items-center justify-between border-b bg-muted/30 px-3 py-2">
                <div className="inline-flex gap-0.5 rounded-md border bg-background p-0.5">
                    <button
                        type="button"
                        onClick={() => setMode('assistant')}
                        className={cn(
                            'rounded px-3 py-1 text-xs',
                            mode === 'assistant'
                                ? 'bg-primary text-primary-foreground font-semibold'
                                : 'text-muted-foreground'
                        )}
                    >
                        Assistant test
                    </button>
                    <button
                        type="button"
                        onClick={() => opcodeTabAvailable && setMode('opcode')}
                        disabled={!opcodeTabAvailable}
                        title={opcodeTabAvailable ? '' : 'Add an opcode to test it directly'}
                        className={cn(
                            'rounded px-3 py-1 text-xs',
                            mode === 'opcode' && opcodeTabAvailable
                                ? 'bg-primary text-primary-foreground font-semibold'
                                : 'text-muted-foreground',
                            !opcodeTabAvailable && 'cursor-not-allowed opacity-50'
                        )}
                    >
                        OpCode test
                    </button>
                </div>
                <div className="flex items-center gap-0.5">
                    {onMaximizeToggle && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onMaximizeToggle}
                            title={maximized ? 'Collapse to side panel' : 'Maximize'}
                        >
                            {maximized
                                ? <Minimize2 className="h-4 w-4" />
                                : <Maximize2 className="h-4 w-4" />}
                        </Button>
                    )}
                    {onClose && (
                        <Button variant="ghost" size="sm" onClick={onClose} title="Hide">
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Active panel */}
            <div className="flex-1 min-h-0 relative">
                {mode === 'assistant' && (
                    <AssistantTestPanel assistant={assistant} maximized={maximized} isDirty={isDirty} onSaveFirst={onSaveFirst} />
                )}
                {mode === 'opcode' && opcodeTabAvailable && (
                    <OpcodeTestPanel
                        assistant={assistant}
                        expandedOpcode={expandedOpcode}
                        opcodeMeta={opcodeMeta}
                        maximized={maximized}
                        isDirty={isDirty}
                        onSaveFirst={onSaveFirst}
                        refreshSignal={refreshSignal}
                    />
                )}
            </div>
        </aside>
    );
}
